import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../services/api';
import { useVerificationStore, type VerificationStep } from '../stores/verificationStore';
import { Layout } from '../components/shared/Layout';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ConsentStep } from '../components/verification/ConsentStep';
import { PersonalInfoStep } from '../components/verification/PersonalInfoStep';
import { OtpStep } from '../components/verification/OtpStep';
import { DocumentUploadStep } from '../components/verification/DocumentUploadStep';
import { SelfieStep } from '../components/verification/SelfieStep';
import { ReviewStep } from '../components/verification/ReviewStep';
import { PaymentStep } from '../components/verification/PaymentStep';

const STEPS: { key: VerificationStep; label: string }[] = [
  { key: 'consent', label: 'Consent' },
  { key: 'personal-info', label: 'Personal Info' },
  { key: 'otp-verification', label: 'Verify Phone' },
  { key: 'id-upload', label: 'ID Document' },
  { key: 'selfie', label: 'Selfie' },
  { key: 'review', label: 'Review' },
  { key: 'payment', label: 'Payment' },
];

export function VerificationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isInitializing, setIsInitializing] = useState(true);
  const {
    currentStep,
    setRequestId,
    setStep,
    reset,
  } = useVerificationStore();

  useEffect(() => {
    const initializeRequest = async () => {
      const existingRequestId = searchParams.get('requestId');
      const step = searchParams.get('step');

      if (existingRequestId) {
        setRequestId(existingRequestId);
        if (step === 'payment') {
          setStep('payment');
        }
      } else {
        // Create new request
        reset();
        try {
          const response = await verificationApi.start();
          setRequestId(response.data.requestId);
        } catch (error) {
          console.error('Failed to create verification request:', error);
          navigate('/dashboard');
          return;
        }
      }
      setIsInitializing(false);
    };

    initializeRequest();
  }, [searchParams, setRequestId, setStep, reset, navigate]);

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setStep(STEPS[nextIndex].key);
    }
  };

  const goToPreviousStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(STEPS[prevIndex].key);
    }
  };

  const handlePaymentComplete = () => {
    navigate('/dashboard');
  };

  const handleCancel = () => {
    reset();
    navigate('/dashboard');
  };

  if (isInitializing) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header with Cancel */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-900">New Verification</h1>
          <button
            onClick={handleCancel}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5 mr-1" />
            Cancel
          </button>
        </div>

        {/* Progress Steps */}
        <nav className="mb-8">
          <ol className="flex items-center">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;

              return (
                <li
                  key={step.key}
                  className={`flex items-center ${index !== STEPS.length - 1 ? 'flex-1' : ''}`}
                >
                  <div className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                        isCompleted
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : isCurrent
                          ? 'border-primary-600 text-primary-600'
                          : 'border-gray-300 text-gray-500'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckIcon className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium hidden sm:block ${
                        isCurrent ? 'text-primary-600' : 'text-gray-500'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index !== STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        isCompleted ? 'bg-primary-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Step Content */}
        <div className="card">
          {currentStep === 'consent' && <ConsentStep onNext={goToNextStep} onCancel={handleCancel} />}
          {currentStep === 'personal-info' && (
            <PersonalInfoStep onNext={goToNextStep} onBack={goToPreviousStep} />
          )}
          {currentStep === 'otp-verification' && (
            <OtpStep onNext={goToNextStep} onBack={goToPreviousStep} />
          )}
          {currentStep === 'id-upload' && (
            <DocumentUploadStep onNext={goToNextStep} onBack={goToPreviousStep} />
          )}
          {currentStep === 'selfie' && (
            <SelfieStep onNext={goToNextStep} onBack={goToPreviousStep} />
          )}
          {currentStep === 'review' && (
            <ReviewStep onNext={goToNextStep} onBack={goToPreviousStep} />
          )}
          {currentStep === 'payment' && <PaymentStep onComplete={handlePaymentComplete} />}
        </div>
      </div>
    </Layout>
  );
}
